import { create } from 'zustand'
import { persist } from 'zustand/middleware'

// 草稿默认过期时间：7 天
export const DEFAULT_DRAFT_EXPIRATION = 7 * 24 * 60 * 60 * 1000

export interface FormDraft<T = unknown> {
  formId: string
  formType: string
  data: T
  savedAt: number
  expiresAt: number
  version: number
}

interface DraftState {
  drafts: Record<string, FormDraft>
  lastCleanupAt: number
  saveDraft: <T>(formId: string, formType: string, data: T, version?: number) => void
  getDraft: <T>(formId: string) => FormDraft<T> | null
  clearDraft: (formId: string) => void
  removeDraft: (formId: string) => void // Alias for clearDraft
  clearAllDrafts: () => void
  clearExpiredDrafts: () => void
  cleanExpiredDrafts: () => void // Alias for clearExpiredDrafts
  hasDraft: (formId: string) => boolean
  getDraftsByType: <T>(formType: string) => FormDraft<T>[]
}

export const useDraftStore = create<DraftState>()(
  persist(
    (set, get) => ({
      drafts: {},
      lastCleanupAt: Date.now(),

      saveDraft: <T>(formId: string, formType: string, data: T, version = 1) => {
        const now = Date.now()
        set((state) => ({
          drafts: {
            ...state.drafts,
            [formId]: {
              formId,
              formType,
              data,
              savedAt: now,
              expiresAt: now + DEFAULT_DRAFT_EXPIRATION,
              version,
            },
          },
        }))
      },

      getDraft: <T>(formId: string) => {
        const { drafts } = get()
        const draft = drafts[formId]
        if (!draft) {
          return null
        }

        // 检查是否过期
        if (Date.now() > draft.expiresAt) {
          // 异步清除过期草稿
          setTimeout(() => get().clearDraft(formId), 0)
          return null
        }

        return draft as FormDraft<T>
      },

      clearDraft: (formId) => {
        set((state) => {
          const remaining = { ...state.drafts }
          delete remaining[formId]
          return { drafts: remaining }
        })
      },

      // Alias for clearDraft (for compatibility)
      removeDraft: (formId) => {
        set((state) => {
          const remaining = { ...state.drafts }
          delete remaining[formId]
          return { drafts: remaining }
        })
      },

      clearAllDrafts: () => {
        set({ drafts: {}, lastCleanupAt: Date.now() })
      },

      clearExpiredDrafts: () => {
        const now = Date.now()
        set((state) => {
          const validDrafts: Record<string, FormDraft> = {}
          for (const [id, draft] of Object.entries(state.drafts)) {
            if (draft.expiresAt > now) {
              validDrafts[id] = draft
            }
          }
          return { drafts: validDrafts, lastCleanupAt: now }
        })
      },

      // Alias for clearExpiredDrafts (for compatibility)
      cleanExpiredDrafts: () => {
        const now = Date.now()
        set((state) => {
          const validDrafts: Record<string, FormDraft> = {}
          for (const [id, draft] of Object.entries(state.drafts)) {
            if (draft.expiresAt > now) {
              validDrafts[id] = draft
            }
          }
          return { drafts: validDrafts, lastCleanupAt: now }
        })
      },

      hasDraft: (formId) => {
        const draft = get().getDraft(formId)
        return draft !== null
      },

      getDraftsByType: <T>(formType: string) => {
        const { drafts } = get()
        const now = Date.now()
        return Object.values(drafts)
          .filter((d) => d.formType === formType && d.expiresAt > now) as FormDraft<T>[]
      },
    }),
    {
      name: 'draft-storage',
      partialize: (state) => ({
        drafts: state.drafts,
        lastCleanupAt: state.lastCleanupAt,
      }),
    }
  )
)

// 应用启动时清理过期草稿
if (typeof window !== 'undefined') {
  setTimeout(() => {
    useDraftStore.getState().clearExpiredDrafts()
  }, 1000)
}
